import { getFunctions, httpsCallable } from 'firebase/functions';

export const cleanAndParseJson = (text: string): any => {
    if (!text) return {};
    
    let jsonString = text;

    const jsonBlockRegex = /```(?:json)?\s*([\s\S]*?)\s*```/;
    const match = text.match(jsonBlockRegex);
    if (match) {
        jsonString = match[1];
    } else {
        const firstOpen = text.indexOf('{');
        const lastClose = text.lastIndexOf('}');
        if (firstOpen !== -1 && lastClose !== -1 && lastClose > firstOpen) {
            jsonString = text.substring(firstOpen, lastClose + 1);
        }
    }

    try {
        jsonString = jsonString.replace(/[\u0000-\u0019]+/g, ""); 
        return JSON.parse(jsonString);
    } catch (e) {
        console.error("Failed to parse JSON response.", e);
        return { 
            reportType: "TextAnalysisResult", 
            title: "Analysis Error", 
            summary: "The system generated a response that could not be processed as structured data.",
            sections: [{ heading: "Raw Output", content: text }]
        };
    }
};

export async function callSecureGemini(prompt: string): Promise<string> {
    const functions = getFunctions();
    
    // Always use Firebase Functions in the frontend client
    const callableFn = httpsCallable(functions, 'generateContent');
    const resultInstance = await callableFn({ 
        contents: [{ role: 'user', parts: [{ text: prompt }] }] 
    });
    
    const resData = resultInstance.data as { text?: string };
    const resultText = resData?.text || "Error: No content generated.";
    
    return resultText;
}

export async function generateFsatAuditFromPlan(completedPlan: any): Promise<any> {
    const prompt = `
    Analyze the following completed Facility Security Assessment (FSAT) responses and generate a JSON audit report.
    ${JSON.stringify(completedPlan)}
    
    Return ONLY a raw JSON format array representing the prioritized mitigation requirements. Do not wrap in markdown tags like \`\`\`json.
    [{ "id": 1, "priority": "High", "finding": "Description", "recommendation": "Required action" }]
    `;

    const resultText = await callSecureGemini(prompt);
    return cleanAndParseJson(resultText);
}

export async function generateFsatAnalysis(formState: any): Promise<any> {
    const prompt = `
    Analyze this FSAT form state and generate a JSON risk analysis report.
    ${JSON.stringify(formState)}
    
    Return ONLY a raw JSON block starting with { and ending with }.
    { "overallRiskLevel": "High", "keyVulnerabilities": [], "mitigationStrategy": "Description" }
    `;

    const resultText = await callSecureGemini(prompt);
    return cleanAndParseJson(resultText);
}

export async function fetchStatesForCountry(country: string): Promise<string[]> {
    const prompt = `List the primary states, provinces, or regions for the country: ${country}. 
    Return ONLY a valid JSON array of strings (e.g., ["State1", "State2"]). 
    Strict requirement: DO NOT include ANY markdown formatting, no backticks, no \`\`\`json blocks, and no introductory or concluding text. 
    The output must be a raw string that can be parsed directly by JSON.parse().`;
    const resultText = await callSecureGemini(prompt);
    
    const json = cleanAndParseJson(resultText);
    return Array.isArray(json) ? json : [];
}

export async function fetchCitiesForState(country: string, state: string): Promise<string[]> {
    const prompt = `List 50 major cities in ${state}, ${country}. 
    Return ONLY a valid JSON array of strings (e.g., ["City1", "City2"]). 
    Strict requirement: DO NOT include ANY markdown formatting, no backticks, no \`\`\`json blocks, and no introductory or concluding text. 
    The output must be a raw string that can be parsed directly by JSON.parse().`;
    const resultText = await callSecureGemini(prompt);
    
    const json = cleanAndParseJson(resultText);
    return Array.isArray(json) ? json : [];
}
